import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate, useParams} from 'react-router-dom';
import Slider, {Settings} from 'react-slick';
import {DeleteOutlined} from '@ant-design/icons';
import {Button, Image, Space, Typography} from 'antd';
import {ReactComponent as Engine} from 'assets/images/engine.svg';
import {ReactComponent as FileDrop} from 'assets/images/file_drop.svg';
import {ReactComponent as Speed} from 'assets/images/speedometer.svg';
import {ReactComponent as Weight} from 'assets/images/weight.svg';
import classNames from 'classnames';
import {Dropzone} from 'components/Dropzone';
import {Section} from 'components/layout/Section';
import {Shell} from 'components/Shell';
import {EquipmentRow} from 'components/ui/EquipmentTable/EquipmentRow';
import {EquipmentTable} from 'components/ui/EquipmentTable/EquipmentTable';
import {FileContainer} from 'components/ui/FileContainer';
import {ParameterIconBox} from 'components/ui/ParameterIconBox';
import {ServiceBox} from 'components/ui/ServiceBox';
import {getKeySpecificationsLabelList, getServiceDataLabelList, SpecWithLabel} from 'helpers/constants/vehicleHelpers';
import {observer} from 'mobx-react-lite';
import {AppRoutePaths} from 'routing/Routes';
import {appState} from 'store/AppStore';
import {VehicleState} from 'store/vehicle/VehicleState';
import {OrderedFileDto} from '../../../../../PRMGApiClient';
import {Spinner} from 'components/ui/Spinner';
import {ReservationBox} from 'components/ui/ReservationBox';

export const VehicleDisplayView: React.FC = observer((): JSX.Element => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {garageId, vehicleId} = useParams();
  const [state] = useState(() => new VehicleState());
  const images = state.item.photos?.photoFiles;
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState<number>(0);

  useEffect(() => {
    vehicleId && state.loadVehicle(parseInt(vehicleId));
  }, [garageId, vehicleId, state]);

  const settings: Settings = {
    dots: false,
    infinite: true,
    speed: 500,
    accessibility: true,
    initialSlide: 0,
    slidesToShow: 4,
    slidesToScroll: 1,
    focusOnSelect: true,
    beforeChange: (current: number, next: number) => setCurrentPhotoIndex(next),
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
          dots: true,
        },
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        },
      },
    ],
  };

  return (
    <Shell
      title={state.item.main ? `${state.item.main.carMakeName} ${state.item.main.carModelName}` : ''}
      backLink={AppRoutePaths.garages.url + `/${garageId}`}
      actionButtons={[
        <Button
          key="edit"
          onClick={() => navigate(AppRoutePaths.garages.url + `/${garageId}/vehicles/${vehicleId}/edit`)}
          size="large"
          className="shell-btn"
        >
          {t('Edit Car')}
        </Button>,
      ]}
    >
      <div className="flex flex-col">
        <div className="flex flex-col gap-4 md:grid md:grid-cols-2">
          <div className="flex flex-col w-full h-[21.25rem] overflow-hidden">
            {images && images[currentPhotoIndex] ? (
              <>
                <img
                  className="w-full h-[15.125rem] object-cover mb-2"
                  src={images[currentPhotoIndex].url ?? undefined}
                  alt={t('Current photo')}
                />
                <div className={classNames('vehicle-slider-container', images.length < 5 && 'slider-noslide')}>
                  <Slider {...settings}>
                    {images.map(({url}, indx) => (
                      <div className="h-[80px] px-1" key={indx}>
                        <img
                          className="h-full w-full object-cover cursor-pointer"
                          src={url ?? undefined}
                          alt={`${t('Car photo nr:')} ${indx}`}
                        />
                      </div>
                    ))}
                  </Slider>
                </div>
              </>
            ) : (
              <div className="vehicle-photo-placeholder" />
            )}
          </div>
          <Section
            title={t('Services')}
            links={[
              {
                linkLabel: t('All services'),
                url: AppRoutePaths.services.url + `?vehicleId=${vehicleId}`,
              },
              {
                linkLabel: t('Add service'),
                url: AppRoutePaths.serviceCreate.url + `/${vehicleId}`,
              },
            ]}
            className="vehicle-section"
          >
            <Space direction="vertical" size="small" className="w-full">
              {state.serviceActions === undefined && <Spinner />}
              {state.serviceActions?.length ? (
                state.serviceActions?.map((service) => <ServiceBox key={service.id} service={service} />)
              ) : (
                <Typography.Text italic>{t('No service actions')}</Typography.Text>
              )}
            </Space>
          </Section>
        </div>
        <div className="flex flex-col gap-4 md:grid md:grid-cols-2">
          <Section
            title={t('Overview')}
            className="w-full h-[160px] flex justify-between"
            contentClassName="flex justify-between"
          >
            <ParameterIconBox
              label={t('Horsepower')}
              value={state.item.tech?.enginePower ?? undefined}
              icon={<Speed />}
            />
            <ParameterIconBox
              label={t('0-100 km/h')}
              value={state.item.tech?.acceleration ?? undefined}
              icon={<Speed />}
            />
            <ParameterIconBox
              label={t('Capacity')}
              value={state.item.tech?.engineCapacity ?? undefined}
              icon={<Engine className="ml-[-0.313rem]" />}
            />
            <ParameterIconBox label={t('Top speed')} value={state.item.tech?.maxSpeed ?? undefined} icon={<Speed />} />
            <ParameterIconBox label={t('Weight')} value={state.item.tech?.mass ?? undefined} icon={<Weight />} />
          </Section>
          <Section
            title={t('Reservations')}
            links={[
              {linkLabel: t('All reservations'), url: AppRoutePaths.reservations.url + `?vehicleId=${vehicleId}`},
              {
                linkLabel: t('Add reservation'),
                url: AppRoutePaths.reservationCreate.url + `/${vehicleId}`,
              },
            ]}
            className="car-services-container w-full h-[160px]"
          >
            <Space direction="vertical" size="small" className="w-full">
              {state.reservations === undefined && <Spinner />}
              {state.reservations?.length ? (
                state.reservations?.map((reservation) => (
                  <ReservationBox key={reservation.id} reservation={reservation} />
                ))
              ) : (
                <Typography.Text italic>{t('No service actions')}</Typography.Text>
              )}
            </Space>
          </Section>
        </div>
        <div className="grid grid-cols-3 gap-4 ">
          <Section
            title={t('Key specification')}
            className="vehicle-section border-r-[0.031rem] border-lightBlue"
            contentClassName="vehicle-spec-content"
          >
            {getKeySpecificationsLabelList(state.item).map(({label, value}, indx) => (
              <SpecElement key={indx} label={label} value={value} />
            ))}
          </Section>
          <Section
            title={t('Service data')}
            className="vehicle-section border-r-[0.031rem] border-lightBlue"
            contentClassName="vehicle-spec-content"
          >
            {getServiceDataLabelList(state.item).map(({label, value}, indx) => (
              <SpecElement key={indx} label={label} value={value} />
            ))}
          </Section>
          <Section
            title={t('Parking spot attributes')}
            className="vehicle-section"
            contentClassName="vehicle-spec-content basis-1/4"
          >
            {appState.attributeDefinitions.map((attr) => (
              <SpecElement
                key={attr.id}
                label={attr.name ?? ''}
                value={
                  state.item.garageSpot?.attributeValues?.find((value) => value.attributeDefinitionId === attr.id)
                    ?.value ?? ''
                }
              />
            ))}
          </Section>
        </div>
        {state.item.garageSpot?.carEquipment && state.item.garageSpot?.carEquipment.length !== 0 && (
          <Section title={t('Car equipment')} className="w-full">
            <EquipmentTable editable={false}>
              {state.item.garageSpot?.carEquipment?.map((equipment, index) => (
                <EquipmentRow key={index} rowData={equipment} />
              ))}
            </EquipmentTable>
          </Section>
        )}
        <div className="flex flex-col gap-4 sm:grid sm:grid-cols-2">
          <Section title={t('Files')} className="w-full flex" contentClassName="vehicle-file-content pt-10">
            <Dropzone
              icon={<FileDrop className="mx-auto h-8 w-8" />}
              className="max-w-[16.5rem] mx-auto mb-4"
              multiple={true}
              onDrop={(acceptedFiles: File[]) => state.updateAttachments(acceptedFiles)}
            />
            <div className="grid grid-cols-2 gap-2 w-fit mx-auto px-2 pb-2">
              {state.item.attachments?.attachments?.map((file) => (
                <FileContainer
                  className="max-w-[100%]"
                  key={file.fileId}
                  file={file}
                  onRemove={() => state.removeAttachment(file.fileId)}
                />
              ))}
            </div>
          </Section>
          <Section
            title={t('Additional photos')}
            className="w-full flex car-services-container"
            contentClassName="vehicle-file-content pt-2"
          >
            <span className="block text-12 leading-4 mb-4 mx-4">{t('Photos for marking damages in reports')}</span>
            <Dropzone
              icon={<FileDrop className="mx-auto h-8 w-8" />}
              className="max-w-[16.5rem] mx-auto mb-4"
              accept={{
                'image/jpeg': [],
                'image/png': [],
              }}
              multiple={true}
              onDrop={(acceptedFiles: File[]) => state.updateModelFiles(acceptedFiles)}
            />
            <div className="grid gap-2 grid-cols-4 px-2 pb-2">
              {state.item.photos?.modelFiles?.map((image: OrderedFileDto) => (
                <div key={image.fileId} className="flex-col justify-center">
                  {image.url && (
                    <>
                      <Image
                        style={{
                          maxHeight: '6rem',
                          minHeight: '6rem',
                          objectFit: 'cover',
                          width: '100%',
                          height: '100%',
                        }}
                        key={image.fileId}
                        src={image.url}
                      />
                      <Button
                        type="link"
                        className="flex gap-1 justify-center"
                        onClick={() => state.removeModelFile(image.fileId)}
                        icon={<DeleteOutlined className="text-lg" />}
                      >
                        {t('Remove')}
                      </Button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </Section>
        </div>
      </div>
    </Shell>
  );
});

const SpecElement: React.FC<SpecWithLabel> = ({label, value}) => {
  const renderItem = () => {
    switch (label) {
      case 'Paint color':
        return appState.colorOptions?.find((item) => Number(item.value) === Number(value))?.label ?? null;
      case 'Color':
        return <div className="h-5 w-[75%] shadow-md" style={{backgroundColor: value ?? 'white'}} />;
      default:
        return <span className="text-14 leading-5 block">{value}</span>;
    }
  };

  return (
    <div className="flex flex-col w-[50%] min-w-[6.875rem] mb-4">
      <span className="text-12 leading-4 text-darkGray">{label}</span>
      {renderItem()}
    </div>
  );
};
